import { observer } from 'mobx-react-lite';
import React, { ReactNode, useEffect, useState } from 'react';
import { Select } from '../../npaper';
import { IProduct, IProductSnapshotOut, useStores } from '../../stores';
import { useDebouncedCallback } from 'use-debounce/lib';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';

export interface IProductSelectProps {
  children?: ReactNode;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  onSelect: (item: IProduct) => void;
  selected?: IProduct['id'];
  filter?: IPaginationFilter<IProductSnapshotOut>['filter'];
  className?: string;
}

export const ProductSelect: React.FC<IProductSelectProps> = observer(
  ({
    children,
    placeholder = 'Vælg et produkt',
    title,
    selected = null,
    disabled = false,
    onSelect,
    className,
    filter = {},
  }) => {
    const { productStore } = useStores();

    const [items, setItems] = useState<IProduct[]>();
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebouncedCallback(setQuery, 150);

    useEffect(() => {
      let isSub = true;
      productStore
        .fetchMore({ filter: { ...filter, deleted: false, search: query } })
        .then((res) => isSub && setItems(res.items));
      return () => {
        isSub = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, query]);

    return (
      <Select
        className={className}
        title={title}
        disabled={disabled}
        placeholder={placeholder}
        onQueryChange={debouncedQuery.callback}
        items={items}
        isLoading={!items || productStore.isLoading}
        resolveSelectedValue={(id) => productStore.getItem(id)}
        itemRenderer={(item) => (
          <div className="flex items-baseline justify-between w-full">
            <p>{item.name}</p>
            {item.id && (
              <p className="p muted small font-mono text-right pl-2 flex-shrink-0">
                {item.sku}
              </p>
            )}
          </div>
        )}
        onSelect={(product) => {
          onSelect && onSelect(product);
        }}
        value={selected}
        valueRenderer={(selected) => selected.name}
      >
        {children}
      </Select>
    );
  }
);
